import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { Plus, Trash2, Power, Check, X, Radio, MapPin, PlayCircle, Crosshair } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const TIERS = ['HATCHBACK', 'SEDAN', 'SUV', 'LUXURY'];

// ─── Live offer ───────────────────────────────────────────────────────────────
// The countdown is derived from the offer's own expiry, not a local timer that
// starts when this component renders — the offer may already be half spent by the
// time the poll picks it up, and a fresh 20s here would lie about the window.
const OfferCard = ({ offer, onRespond, busy }) => {
  const [secondsLeft, setSecondsLeft] = useState(offer.secondsLeft);

  useEffect(() => {
    setSecondsLeft(offer.secondsLeft);
    const id = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [offer.driverId, offer.tripId, offer.secondsLeft]);

  const total = offer.timeoutSeconds || 20;
  const pct = Math.max(0, Math.min(100, (secondsLeft / total) * 100));
  const expired = secondsLeft <= 0;

  return (
    <div className="card p-5 border-l-4 border-blue-500">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Offer in flight</p>
          <h3 className="text-lg font-bold text-gray-900">
            {offer.driverName || offer.driverId}
            {offer.isSimulated && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 text-xs font-medium">sim</span>
            )}
          </h3>
        </div>
        <div className={`text-3xl font-bold tabular-nums ${expired ? 'text-gray-300' : secondsLeft <= 5 ? 'text-red-500' : 'text-gray-900'}`}>
          {secondsLeft}s
        </div>
      </div>

      <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden mb-4">
        <div
          className={`h-full transition-all duration-1000 ease-linear ${secondsLeft <= 5 ? 'bg-red-500' : 'bg-blue-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="text-xs text-gray-500 space-y-1 mb-4">
        <div className="flex justify-between"><span>Trip</span><span className="font-medium text-gray-700">{offer.rideId || offer.tripId}</span></div>
        {offer.pickup && <div className="flex justify-between"><span>Pickup</span><span className="font-medium text-gray-700 truncate ml-4">{offer.pickup}</span></div>}
        {offer.fare != null && <div className="flex justify-between"><span>Fare</span><span className="font-medium text-gray-700">₹{offer.fare}</span></div>}
      </div>

      {/* Answering after the window closes is what a real late tap does: the engine
          has already moved on and refuses. Left enabled so that path is testable. */}
      <div className="flex gap-3">
        <button
          className="btn btn-secondary flex-1"
          disabled={busy}
          onClick={() => onRespond(offer, 'reject')}
        >
          <X className="h-4 w-4 mr-1" /> Reject
        </button>
        <button
          className="btn btn-primary flex-1"
          disabled={busy}
          onClick={() => onRespond(offer, 'accept')}
        >
          <Check className="h-4 w-4 mr-1" /> Accept
        </button>
      </div>
      {expired && (
        <p className="text-xs text-amber-600 mt-2">
          Window closed — the engine has likely offered the next driver already.
        </p>
      )}
    </div>
  );
};

// ─── Engine state ─────────────────────────────────────────────────────────────
// The engine's own view of the loop, not Node's observation of it. Shows the
// retry round and the offered/excluded sets, which is where re-dispatch is
// actually visible: each reject adds a driver to `excluded` and the next
// candidate appears in `offered`.
const MatchState = ({ state }) => {
  if (!state) return null;
  return (
    <div className="card p-4">
      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Engine state</h4>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
        <div className="flex justify-between"><span className="text-gray-500">Status</span><span className="font-medium">{state.status}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Cycle</span><span className="font-medium">{state.cycle_active ? 'running' : 'idle'}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Retry round</span><span className="font-medium">{state.retry_round ?? 0} / {state.max_retries ?? '-'}</span></div>
        <div className="flex justify-between"><span className="text-gray-500">Offer left</span><span className="font-medium">{state.offer_seconds_left != null ? `${state.offer_seconds_left}s` : '—'}</span></div>
      </div>
      <div className="mt-3 space-y-1 text-xs">
        <div><span className="text-gray-500">Offered:</span> <span className="font-medium">{(state.offered || []).length ? state.offered.join(', ') : '—'}</span></div>
        <div><span className="text-gray-500">Excluded:</span> <span className="font-medium text-amber-600">{(state.excluded || []).length ? state.excluded.join(', ') : '—'}</span></div>
        {state.driver_id && (
          <div><span className="text-gray-500">Assigned:</span> <span className="font-medium text-green-600">{state.driver_id}</span></div>
        )}
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const DriverSim = () => {
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState({ count: 3, latitude: '', longitude: '', vehicleType: 'SEDAN', radiusKm: 3 });
  const [log, setLog] = useState([]);
  const [watchTripId, setWatchTripId] = useState(null);
  const [gpsFor, setGpsFor] = useState(null); // driver row with the GPS panel open
  const [gps, setGps] = useState({ latitude: '', longitude: '' });
  const seenRef = useRef(new Set());

  // 2s: the whole point is a 20s window, so a slower poll would burn a quarter of
  // it before the offer even appears.
  const { data: offersRes } = useQuery(['driver-sim-offers'], () => api.get('/driver-sim/offers'), {
    refetchInterval: 2000,
    refetchIntervalInBackground: false,
  });
  // Memoised: a fresh [] each render would re-fire the log effect below forever.
  const offers = useMemo(() => offersRes?.data?.offers || [], [offersRes]);

  const { data: driversRes } = useQuery(['driver-sim-drivers'], () => api.get('/driver-sim/drivers'));
  const drivers = driversRes?.data?.drivers || [];

  const { data: tripsRes } = useQuery(['driver-sim-trips'], () => api.get('/driver-sim/trips'), {
    refetchInterval: 3000,
  });
  const trips = tripsRes?.data?.trips || [];

  // Follow one trip's loop. Polled at 1s because the countdown and the
  // offered/excluded sets are the whole point of watching.
  const { data: stateRes } = useQuery(
    ['driver-sim-state', watchTripId],
    () => api.get(`/driver-sim/state/${watchTripId}`),
    { enabled: !!watchTripId, refetchInterval: 1000 },
  );
  const matchState = stateRes?.data?.success ? stateRes.data.state : null;

  // A running record of who was offered what, so the re-dispatch chain is visible
  // after the fact rather than only in the 20s it is on screen.
  useEffect(() => {
    offers.forEach((o) => {
      const key = `${o.tripId}:${o.driverId}:${o.offeredAt}`;
      if (seenRef.current.has(key)) return;
      seenRef.current.add(key);
      setLog((l) => [{ at: new Date().toLocaleTimeString(), text: `Offered to ${o.driverName || o.driverId}`, tone: 'info' }, ...l].slice(0, 40));
    });
  }, [offers]);

  const respond = useMutation(
    ({ offer, response }) => api.post('/driver-sim/respond', {
      tripId: offer.tripId, driverId: offer.driverId, offerId: offer.offerId, response,
    }),
    {
      onSuccess: (res, vars) => {
        const body = res?.data;
        const who = vars.offer.driverName || vars.offer.driverId;
        if (body?.success) {
          toast.success(body.message || 'Sent');
          setLog((l) => [{ at: new Date().toLocaleTimeString(), text: `${who} ${vars.response}ed — ${body.message}`, tone: vars.response === 'accept' ? 'ok' : 'warn' }, ...l].slice(0, 40));
        } else {
          toast.error(body?.message || 'Engine refused');
          setLog((l) => [{ at: new Date().toLocaleTimeString(), text: `${who} ${vars.response} refused — ${body?.message}`, tone: 'err' }, ...l].slice(0, 40));
        }
        qc.invalidateQueries(['driver-sim-offers']);
      },
      onError: (e) => toast.error(e?.response?.data?.message || 'Request failed'),
    },
  );

  const createDrivers = useMutation((payload) => api.post('/driver-sim/drivers', payload), {
    onSuccess: (res) => {
      toast.success(`Created ${res?.data?.created ?? 0} drivers`);
      qc.invalidateQueries(['driver-sim-drivers']);
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'Create failed'),
  });

  const toggleDriver = useMutation(({ id, online }) => api.patch(`/driver-sim/drivers/${id}`, { online }), {
    onSuccess: () => qc.invalidateQueries(['driver-sim-drivers']),
  });

  const setLocation = useMutation(({ id, latitude, longitude }) =>
    api.patch(`/driver-sim/drivers/${id}/location`, { latitude, longitude }), {
    onSuccess: (res) => {
      const body = res?.data;
      if (body?.indexed === false) {
        toast.error('Moved, but the engine index was not updated — is Redis reachable?');
      } else {
        toast.success('Driver moved');
      }
      setGpsFor(null);
      qc.invalidateQueries(['driver-sim-drivers']);
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'Move failed'),
  });

  const createTrip = useMutation((payload) => api.post('/driver-sim/trip', payload), {
    onSuccess: (res) => {
      const body = res?.data;
      if (!body?.success) return toast.error(body?.message || 'Could not create trip');
      toast.success(`Trip ${body.rideId} dispatched`);
      setWatchTripId(body.tripId);
      setLog((l) => [{ at: new Date().toLocaleTimeString(), text: `Trip ${body.rideId} created and handed to the engine`, tone: 'info' }, ...l].slice(0, 40));
      qc.invalidateQueries(['driver-sim-trips']);
    },
    onError: (e) => toast.error(e?.response?.data?.message || 'Create failed'),
  });

  const clearTrips = useMutation(() => api.delete('/driver-sim/trips'), {
    onSuccess: (res) => {
      toast.success(`Removed ${res?.data?.deleted ?? 0} simulated trips`);
      setWatchTripId(null);
      qc.invalidateQueries(['driver-sim-trips']);
    },
  });

  const deleteAll = useMutation(() => api.delete('/driver-sim/drivers'), {
    onSuccess: (res) => {
      toast.success(`Removed ${res?.data?.deleted ?? 0} simulated drivers`);
      qc.invalidateQueries(['driver-sim-drivers']);
    },
  });

  if (!hasPermission('driverSimulator', 'view')) {
    return (
      <div className="empty-state">
        <h3 className="empty-state-title">Access Denied</h3>
        <p className="empty-state-description">You don't have permission to use the driver simulator.</p>
      </div>
    );
  }
  const canWrite = hasPermission('driverSimulator', 'write');

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <h1 className="page-title">Driver Simulator</h1>
          <p className="page-subtitle">
            Answer dispatch offers as a driver. Accept and Reject go through the live matching
            engine, so rejecting really does hand the trip to the next candidate.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Live offers ── */}
        <div className="space-y-4">
          {canWrite && (
            <div className="card p-5 space-y-3">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <PlayCircle className="h-4 w-4 text-green-600" /> Simulate a trip
              </h3>
              <p className="text-xs text-gray-500">
                Creates a real PENDING acting-driver trip at this pickup and hands it to the
                engine, exactly as a booking does. Dispatch starts immediately.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Pickup latitude</label>
                  <input className="input" placeholder="11.1085" value={form.latitude}
                    onChange={(e) => setForm({ ...form, latitude: e.target.value })} />
                </div>
                <div>
                  <label className="form-label">Pickup longitude</label>
                  <input className="input" placeholder="77.3411" value={form.longitude}
                    onChange={(e) => setForm({ ...form, longitude: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-3">
                <button className="btn btn-primary flex-1" disabled={createTrip.isLoading}
                  onClick={() => createTrip.mutate({
                    latitude: Number(form.latitude),
                    longitude: Number(form.longitude),
                    vehicleType: form.vehicleType,
                    actingDriverHours: 4,
                  })}>
                  <PlayCircle className="h-4 w-4 mr-1" /> Book &amp; dispatch
                </button>
                {trips.length > 0 && (
                  <button className="btn btn-secondary" disabled={clearTrips.isLoading}
                    onClick={() => { if (window.confirm(`Remove all ${trips.length} simulated trips?`)) clearTrips.mutate(); }}>
                    <Trash2 className="h-4 w-4 mr-1" /> Clear
                  </button>
                )}
              </div>
              {trips.length > 0 && (
                <div className="pt-2 border-t border-gray-100 space-y-1">
                  {trips.slice(0, 5).map((tr) => (
                    <button key={tr._id}
                      onClick={() => setWatchTripId(tr._id)}
                      className={`w-full flex items-center justify-between text-xs px-2 py-1.5 rounded ${watchTripId === tr._id ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-600'}`}>
                      <span className="font-medium">{tr.rideId}</span>
                      <span>{tr.status}{tr.driverId ? ' · assigned' : ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <MatchState state={matchState} />

          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <Radio className="h-4 w-4 text-blue-500" /> Live offers
          </h3>

          {offers.length === 0 ? (
            <div className="card p-8 text-center text-sm text-gray-400">
              No offer in flight. Book an acting-driver trip in the app — when the engine
              offers it to a driver, it appears here with the countdown.
            </div>
          ) : (
            offers.map((o) => (
              <OfferCard
                key={`${o.tripId}:${o.driverId}`}
                offer={o}
                busy={respond.isLoading}
                onRespond={(offer, response) => respond.mutate({ offer, response })}
              />
            ))
          )}

          {log.length > 0 && (
            <div className="card p-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Dispatch log</h4>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {log.map((l, i) => (
                  <div key={i} className="flex gap-2 text-xs">
                    <span className="text-gray-400 tabular-nums">{l.at}</span>
                    <span className={
                      l.tone === 'ok' ? 'text-green-600'
                        : l.tone === 'warn' ? 'text-amber-600'
                          : l.tone === 'err' ? 'text-red-600' : 'text-gray-600'
                    }>{l.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Simulated drivers ── */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-800">Simulated drivers</h3>

          {canWrite && (
            <div className="card p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">How many</label>
                  <input type="number" min="1" max="20" className="input" value={form.count}
                    onChange={(e) => setForm({ ...form, count: e.target.value })} />
                </div>
                <div>
                  <label className="form-label">Vehicle</label>
                  <select className="input" value={form.vehicleType}
                    onChange={(e) => setForm({ ...form, vehicleType: e.target.value })}>
                    {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Latitude</label>
                  <input className="input" placeholder="11.1085" value={form.latitude}
                    onChange={(e) => setForm({ ...form, latitude: e.target.value })} />
                </div>
                <div>
                  <label className="form-label">Longitude</label>
                  <input className="input" placeholder="77.3411" value={form.longitude}
                    onChange={(e) => setForm({ ...form, longitude: e.target.value })} />
                </div>
                <div>
                  <label className="form-label">Spread (km)</label>
                  <input type="number" min="0.5" step="0.5" className="input" value={form.radiusKm}
                    onChange={(e) => setForm({ ...form, radiusKm: e.target.value })} />
                  <p className="text-xs text-gray-400 mt-1">
                    Placed on a ring, so reach-cost ranking has something to order.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <button className="btn btn-primary flex-1" disabled={createDrivers.isLoading}
                  onClick={() => createDrivers.mutate({
                    count: Number(form.count),
                    latitude: Number(form.latitude),
                    longitude: Number(form.longitude),
                    vehicleType: form.vehicleType,
                    radiusKm: Number(form.radiusKm),
                  })}>
                  <Plus className="h-4 w-4 mr-1" /> Create
                </button>
                {drivers.length > 0 && (
                  <button className="btn btn-secondary" disabled={deleteAll.isLoading}
                    onClick={() => { if (window.confirm(`Remove all ${drivers.length} simulated drivers?`)) deleteAll.mutate(); }}>
                    <Trash2 className="h-4 w-4 mr-1" /> Remove all
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="card">
            {drivers.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">No simulated drivers yet.</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['Driver', 'Vehicle', 'Status', 'Trip', ''].map((h) => (
                      <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {drivers.map((d) => (
                    <tr key={d._id}>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">{d.name}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{d.vehicleType || '-'}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${d.status === 'online' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {d.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">{d.tripStatus}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {canWrite && (
                          <>
                            <button className="p-1.5 text-gray-500 hover:text-blue-600" title="Set GPS position"
                              onClick={() => {
                                setGpsFor(gpsFor === d._id ? null : d._id);
                                setGps({
                                  latitude: d.location ? String(d.location[1]) : '',
                                  longitude: d.location ? String(d.location[0]) : '',
                                });
                              }}>
                              <MapPin className="h-4 w-4" />
                            </button>
                            <button className="p-1.5 text-gray-500 hover:text-amber-600" title="Toggle online"
                              onClick={() => toggleDriver.mutate({ id: d._id, online: d.status !== 'online' })}>
                              <Power className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* GPS panel. Writing the position updates BOTH the document and the
                engine's geo index — moving only the document would leave dispatch
                ranking this driver from where they used to be. */}
            {gpsFor && (
              <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                  <Crosshair className="h-4 w-4 text-blue-500" />
                  GPS position — {drivers.find((d) => d._id === gpsFor)?.name}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input className="input" placeholder="Latitude" value={gps.latitude}
                    onChange={(e) => setGps({ ...gps, latitude: e.target.value })} />
                  <input className="input" placeholder="Longitude" value={gps.longitude}
                    onChange={(e) => setGps({ ...gps, longitude: e.target.value })} />
                </div>
                <div className="flex gap-2">
                  <button className="btn btn-secondary flex-1 text-xs"
                    onClick={() => setGps({ latitude: form.latitude, longitude: form.longitude })}>
                    Use pickup point
                  </button>
                  <button className="btn btn-primary flex-1 text-xs" disabled={setLocation.isLoading}
                    onClick={() => setLocation.mutate({
                      id: gpsFor,
                      latitude: Number(gps.latitude),
                      longitude: Number(gps.longitude),
                    })}>
                    Move driver
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverSim;
